import { homedir } from 'node:os';
import { join } from 'node:path';
import { env, AutoModelForSequenceClassification, AutoTokenizer } from '@huggingface/transformers';
import type { RerankerConfig } from '../types/config.js';

// Share cache directory with embeddings
env.cacheDir = join(homedir(), '.cache', 'huggingface-transformers');

/** Debug diagnostics gated by BK_DEBUG_RERANKER=1. Zero overhead when off. */
const DEBUG_RERANKER = process.env['BK_DEBUG_RERANKER'] === '1';

interface RerankerDebugStats {
  totalQueries: number;
  top1Reordered: number;
  top3Reordered: number;
  scoreVariancePositive: number;
}

const debugStats: RerankerDebugStats = {
  totalQueries: 0,
  top1Reordered: 0,
  top3Reordered: 0,
  scoreVariancePositive: 0,
};

// Auto-log aggregate stats on process exit when debug is enabled
if (DEBUG_RERANKER) {
  process.on('beforeExit', () => {
    RerankerService.logDebugStats();
  });
}

export interface RerankerCandidate {
  readonly id: string;
  readonly content: string;
  readonly score: number;
}

export interface RerankerResult {
  readonly id: string;
  readonly originalScore: number;
  readonly rerankerScore: number;
}

export interface RerankerOutput {
  readonly results: readonly RerankerResult[];
  readonly timeMs: number;
}

export class RerankerService {
  private model: Awaited<
    ReturnType<typeof AutoModelForSequenceClassification.from_pretrained>
  > | null = null;
  private tokenizer: Awaited<ReturnType<typeof AutoTokenizer.from_pretrained>> | null = null;
  private initPromise: Promise<void> | null = null;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly -- mutated in dispose()
  private disposed = false;
  private readonly config: RerankerConfig;

  constructor(config: RerankerConfig) {
    this.config = config;
  }

  /**
   * Guard against use-after-dispose
   */
  private assertNotDisposed(): void {
    if (this.disposed) {
      throw new Error('RerankerService has been disposed');
    }
  }

  /**
   * Check if reranking is enabled
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Initialize the reranker model (concurrency-safe).
   * Multiple concurrent calls will share the same initialization promise.
   */
  async initialize(): Promise<void> {
    this.assertNotDisposed();
    if (this.model !== null && this.tokenizer !== null) return;

    this.initPromise ??= (async (): Promise<void> => {
      try {
        // Load model and tokenizer in parallel
        const [model, tokenizer] = await Promise.all([
          AutoModelForSequenceClassification.from_pretrained(this.config.model, {
            dtype: 'fp32',
          }),
          AutoTokenizer.from_pretrained(this.config.model),
        ]);
        this.model = model;
        this.tokenizer = tokenizer;
      } catch (error) {
        // Allow retry on failure
        this.initPromise = null;
        throw error;
      }
    })();
    await this.initPromise;
  }

  /**
   * Rerank candidates by scoring query-document pairs with the cross-encoder.
   * Returns results sorted by reranker score (descending).
   */
  async rerank(query: string, candidates: readonly RerankerCandidate[]): Promise<RerankerOutput> {
    this.assertNotDisposed();
    const startTime = Date.now();

    if (!this.config.enabled) {
      // Pass through without reranking
      return {
        results: candidates.map((c) => ({
          id: c.id,
          originalScore: c.score,
          rerankerScore: c.score,
        })),
        timeMs: Date.now() - startTime,
      };
    }

    if (candidates.length === 0) {
      return { results: [], timeMs: Date.now() - startTime };
    }

    // Ensure model is loaded
    if (this.model === null || this.tokenizer === null) {
      await this.initialize();
    }
    if (this.model === null || this.tokenizer === null) {
      throw new Error('Failed to initialize reranker model');
    }

    // Limit to topK candidates
    const toRerank = candidates.slice(0, this.config.topK);

    // Score each query-document pair
    const scoredResults: RerankerResult[] = [];

    for (const candidate of toRerank) {
      const score = await this.scoreQueryDocPair(query, candidate.content);
      scoredResults.push({
        id: candidate.id,
        originalScore: candidate.score,
        rerankerScore: score,
      });
    }

    // Capture pre-rerank order for debug comparison
    const preRankIds = scoredResults.map((r) => r.id);

    // Sort by reranker score (descending)
    scoredResults.sort((a, b) => b.rerankerScore - a.rerankerScore);

    if (DEBUG_RERANKER) {
      const postRankIds = scoredResults.map((r) => r.id);
      debugStats.totalQueries++;

      // Check if top-1 or top-3 changed
      if (preRankIds[0] !== postRankIds[0]) debugStats.top1Reordered++;
      const preTop3 = preRankIds.slice(0, 3).join(',');
      const postTop3 = postRankIds.slice(0, 3).join(',');
      if (preTop3 !== postTop3) debugStats.top3Reordered++;

      // Check score variance
      const scores = scoredResults.map((r) => r.rerankerScore);
      const mean = scores.reduce((a, b) => a + b, 0) / scores.length;
      const variance = scores.reduce((a, b) => a + (b - mean) ** 2, 0) / scores.length;
      if (variance > 1e-10) debugStats.scoreVariancePositive++;

      // Per-query detail for first 3 queries (stderr for immediate flush)
      if (debugStats.totalQueries <= 3) {
        console.error(
          `[DEBUG_RERANKER] query #${String(debugStats.totalQueries)}: ` +
            `pre=[${preRankIds.slice(0, 3).join(', ')}] ` +
            `post=[${postRankIds.slice(0, 3).join(', ')}] ` +
            `scores=[${scores
              .slice(0, 3)
              .map((s) => s.toFixed(4))
              .join(', ')}] ` +
            `variance=${variance.toFixed(6)}`
        );
      }
    }

    // Return top returnK
    const finalResults = scoredResults.slice(0, this.config.returnK);

    return {
      results: finalResults,
      timeMs: Date.now() - startTime,
    };
  }

  /**
   * Score a single query-document pair using the cross-encoder.
   */
  private async scoreQueryDocPair(query: string, document: string): Promise<number> {
    if (this.model === null || this.tokenizer === null) {
      throw new Error('Model not initialized');
    }

    // Tokenize the query-document pair
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- transformers.js dynamic types
    const inputs = await this.tokenizer([query], {
      text_pair: [document],
      padding: true,
      truncation: true,
      max_length: 512,
    });

    // Run through the model
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- transformers.js dynamic types
    const output = await this.model(inputs);

    // Extract score from logits - transformers.js returns dynamic tensor types
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/consistent-type-assertions -- transformers.js dynamic types
    const logits = output.logits as { data: ArrayLike<number> };

    // MS MARCO models output a single logit for relevance
    // Use Array.from to safely convert tensor data to number array
    const scores = Array.from(logits.data);
    const score = scores[0];
    if (typeof score !== 'number') {
      throw new Error('Invalid reranker output: expected numeric score');
    }
    return score;
  }

  /**
   * Check if the reranker is initialized.
   */
  isInitialized(): boolean {
    return this.model !== null && this.tokenizer !== null;
  }

  /**
   * Check if this service has been disposed.
   */
  isDisposed(): boolean {
    return this.disposed;
  }

  /**
   * Reset the service to uninitialized state, allowing reuse after disposal.
   */
  async reset(): Promise<void> {
    if (this.model !== null) {
      await this.model.dispose();
      this.model = null;
    }
    this.tokenizer = null;
    this.initPromise = null;
    this.disposed = false;
  }

  /**
   * Log aggregate reranker debug stats. Only meaningful when BK_DEBUG_RERANKER=1.
   */
  static logDebugStats(): void {
    if (!DEBUG_RERANKER || debugStats.totalQueries === 0) return;
    const pct = (n: number): string => ((n / debugStats.totalQueries) * 100).toFixed(1);
    // stderr for immediate flush (pino async transport may not flush before process.exit)
    console.error(
      `[DEBUG_RERANKER] Aggregate: ${String(debugStats.totalQueries)} queries, ` +
        `top1 reordered: ${pct(debugStats.top1Reordered)}% (${String(debugStats.top1Reordered)}/${String(debugStats.totalQueries)}), ` +
        `top3 reordered: ${pct(debugStats.top3Reordered)}% (${String(debugStats.top3Reordered)}/${String(debugStats.totalQueries)}), ` +
        `score variance>0: ${pct(debugStats.scoreVariancePositive)}% (${String(debugStats.scoreVariancePositive)}/${String(debugStats.totalQueries)})`
    );
  }

  /**
   * Dispose the reranker to free resources.
   */
  async dispose(): Promise<void> {
    if (this.model !== null) {
      await this.model.dispose();
      this.model = null;
    }
    this.tokenizer = null;
    this.initPromise = null;
    this.disposed = true;
  }
}
